import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '@packages/backend/convex/_generated/api';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { SettingsDrawer } from '@/components/SettingsDrawer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

type OnboardingSearch = {
  o?: string;
  d?: string;
};

export const Route = createFileRoute('/_authenticated/onboarding')({
  validateSearch: (search: Record<string, unknown>): OnboardingSearch => ({
    o: typeof search.o === 'string' ? search.o : undefined,
    d: typeof search.d === 'string' ? search.d : undefined,
  }),
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const language = getLocale();
  const { o: orgSlug, d: departmentSlug } = Route.useSearch();
  const profile = useQuery(api.tutor.queries.getMyProfile);

  const saveOnboardingProfile = useMutation(
    api.tutor.mutations.saveOnboardingProfile,
  );
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);

  // Validation errors
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Guard: If already onboarded, redirect to /new.
  useEffect(() => {
    if (!profile?.onboardingCompletedAt) return;
    navigate({ to: '/new' });
  }, [profile, navigate]);

  // Loading state
  if (profile === undefined) {
    return <LoadingSkeleton />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    setFirstNameError(false);
    setLastNameError(false);
    setConsentError(false);
    setOnboardingError(null);

    // Validate
    let hasError = false;

    if (!firstName.trim()) {
      setFirstNameError(true);
      hasError = true;
    }

    if (!lastName.trim()) {
      setLastNameError(true);
      hasError = true;
    }

    if (!privacyConsent) {
      setConsentError(true);
      hasError = true;
    }

    if (hasError) return;

    setIsSubmitting(true);

    try {
      await saveOnboardingProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        privacyConsent,
        preferredLanguage: language,
        latestOrgSlug: orgSlug,
        departmentSlug,
      });

      setOnboardingError(null);
      navigate({ to: '/new' });
    } catch (err) {
      console.error('Failed to save onboarding profile:', err);
      Sentry.withScope((scope) => {
        scope.setTag('tutor.flow', 'onboarding');
        Sentry.captureException(err);
      });
      setOnboardingError(m.onboarding_saveError());
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 pt-8 pb-12 sm:px-6 lg:px-8">
      <div className="flex justify-center">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-start text-2xl font-bold">
                {m.onboarding_title()}
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {onboardingError && (
                  <Alert variant="destructive">
                    <AlertDescription>{onboardingError}</AlertDescription>
                  </Alert>
                )}

                {/* First Name */}
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    {m.onboarding_firstName_label()}
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (firstNameError) setFirstNameError(false);
                    }}
                    autoFocus
                  />
                  {firstNameError && (
                    <p className="text-sm text-destructive">
                      {m.onboarding_firstName_required()}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    {m.onboarding_lastName_label()}
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      if (lastNameError) setLastNameError(false);
                    }}
                  />
                  {lastNameError && (
                    <p className="text-sm text-destructive">
                      {m.onboarding_lastName_required()}
                    </p>
                  )}
                </div>

                {/* Privacy Consent */}
                <div className="space-y-2 my-6">
                  <Card
                    className={`flex p-2 items-start gap-3 border-primary ${
                      privacyConsent
                        ? 'ring-1 ring-primary bg-primary/20'
                        : 'bg-primary/5'
                    }`}
                  >
                    <CardContent className="space-y-2 p-1 flex items-start gap-3">
                      <Checkbox
                        id="privacyConsent"
                        checked={privacyConsent}
                        onCheckedChange={(checked) => {
                          setPrivacyConsent(checked === true);
                          if (checked) setConsentError(false);
                        }}
                        className="mt-0.5 self-start"
                      />
                      <Label
                        htmlFor="privacyConsent"
                        className="text-sm leading-snug font-normal cursor-pointer inline"
                      >
                        {m.newPatient_privacyConsentPrefix()}
                        <Link
                          to="/privacy"
                          className="inline text-primary underline underline-offset-2 hover:text-primary/80"
                          target="_blank"
                        >
                          {m.newPatient_privacyPolicyLink()}
                        </Link>
                        {m.newPatient_privacyConsentSuffix()}
                      </Label>
                    </CardContent>
                  </Card>
                  {consentError && (
                    <p className="text-sm text-destructive">
                      {m.newPatient_privacyConsentRequired()}
                    </p>
                  )}
                </div>
              </CardContent>

              <CardFooter>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? m.common_loading() : m.onboarding_continue()}
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 flex justify-center pb-6">
        <SettingsDrawer />
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 px-4 pt-8 pb-12 sm:px-6 lg:px-8">
      <div className="flex justify-center">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
              <Skeleton className="h-20 w-full" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-11 w-full" />
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}
